import type {
  AiChatMessageDto,
  AiChatSessionSummaryDto,
} from '@asset-metrix/portal-gateway-client-js';

import { PORTAL_GATEWAY_API_BASE_URL } from '../../config';
import { HttpError } from '../errors';
import { portalGatewayClient } from '../portalGatewayClient';

import {
  type DocumentChatEvent,
  streamPortalGatewayChat,
} from './documentChat';

const SESSION_PAGE_SIZE = 100;
const HISTORY_PAGE_SIZE = 200;

export type StreamAiAssistantChatParams = {
  onComplete: () => void;
  onError: (error: Error) => void;
  onEvent: (event: DocumentChatEvent) => void;
  prompt: string;
  sessionId?: string;
};

export async function fetchAiChatSessions(): Promise<
  AiChatSessionSummaryDto[]
> {
  const sessions: AiChatSessionSummaryDto[] = [];
  let offset = 0;
  let totalCount = 0;

  do {
    const response = await portalGatewayClient.ai.listAiChatSessions({
      offset,
      limit: SESSION_PAGE_SIZE,
      sort: 'updated_at:desc',
    });
    if (!response.ok) {
      throw new HttpError('Fetching AI chat sessions failed.', response);
    }

    const page = response.data.data;
    sessions.push(...page.items);
    totalCount = page.meta.totalCount;
    if (page.meta.resultCount === 0) {
      break;
    }
    offset += page.meta.resultCount;
  } while (offset < totalCount);

  return sessions;
}

export async function fetchAiChatSessionHistory(
  sessionId: string
): Promise<AiChatMessageDto[]> {
  const messages: AiChatMessageDto[] = [];
  let offset = 0;
  let totalCount = 0;

  do {
    const response = await portalGatewayClient.ai.getAiChatSessionHistory(
      sessionId,
      {
        offset,
        limit: HISTORY_PAGE_SIZE,
        sort: 'timestamp:asc',
      }
    );
    if (!response.ok) {
      throw new HttpError('Fetching AI chat history failed.', response);
    }

    const page = response.data.data;
    messages.push(...page.items);
    totalCount = page.meta.totalCount;
    if (page.meta.resultCount === 0) {
      break;
    }
    offset += page.meta.resultCount;
  } while (offset < totalCount);

  return messages;
}

export async function deleteAiChatSession(sessionId: string): Promise<void> {
  const response = await portalGatewayClient.ai.deleteAiChatSession(sessionId);
  if (!response.ok) {
    throw new HttpError('Deleting AI chat session failed.', response);
  }
}

export function streamAiAssistantChat({
  prompt,
  sessionId,
  onComplete,
  onError,
  onEvent,
}: StreamAiAssistantChatParams): { abort: () => void } {
  return streamPortalGatewayChat({
    url: `${PORTAL_GATEWAY_API_BASE_URL}/portal-gateway/v1/tenant/ai/chat`,
    body: {
      prompt,
      ...(sessionId ? { sessionId } : {}),
    },
    onComplete,
    onError,
    onEvent,
  });
}
